import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ChevronDown, ChevronUp, Package, MapPin } from 'lucide-react';
import { API_URL } from '../utils/cartApi';

const AdminOrders = () => {
  const [orders, setOrders] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/orders/all`);
        setOrders(res.data.orders || res.data || []);
      } catch (err) {
        console.error('Failed loading orders:', err);
        setError('Unable to load orders at this time.');
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, []);

  const updateStatus = (id, status) => {
    axios.put(`${API_URL}/api/orders/update-status/${id}`, { status }).then(() => {
      setOrders(orders.map(o => o._id === id ? { ...o, status } : o));
    });
  };

  return (
    <div className="p-6 bg-[#f5f2e8] min-h-screen">
      <h2 className="text-2xl font-bold text-maroon mb-6 border-b-2 border-maroon pb-2">ORDERS MANAGEMENT</h2>
      <div className="bg-white border rounded shadow-sm overflow-hidden">
        <table className="w-full text-left text-sm">
          <thead className="bg-gray-100 text-maroon font-bold uppercase text-xs border-b">
            <tr>
              <th className="p-4">Order ID</th>
              <th>Customer Name</th>
              <th>Phone</th>
              <th>Total Amount</th>
              <th>Status</th>
              <th>Payment ID</th>
              <th>Date</th>
              <th>Items</th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan="8" className="p-8 text-center text-gray-500 italic">
                  {loading ? 'Loading orders...' : error ? error : 'No orders found.'}
                </td>
              </tr>
            ) : orders.map(order => (
              <React.Fragment key={order._id}>
                <tr className="border-b hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedId(expandedId === order._id ? null : order._id)}>
                  <td className="p-4 font-mono font-bold text-maroon">{order._id}</td>
                  <td className="font-bold">{order.customer?.name || 'Unknown'}</td>
                  <td>{order.customer?.phone || '—'}</td>
                  <td className="font-bold text-lg">₹{order.total}</td>
                  <td>
                    <select 
                      value={order.status} 
                      onChange={(e) => updateStatus(order._id, e.target.value)}
                      className="border rounded px-2 py-1 text-xs font-bold bg-white outline-none"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <option value="Order Placed">Order Placed</option>
                      <option value="Paid">Paid</option>
                      <option value="Processing">Processing</option>
                      <option value="Shipped">Shipped</option>
                      <option value="Delivered">Delivered</option>
                      <option value="Cancelled">Cancelled</option>
                    </select>
                  </td>
                  <td className="font-mono text-xs">{order.paymentId}</td>
                  <td className="text-xs">{order.createdAt ? new Date(order.createdAt).toLocaleDateString() : '—'}</td>
                  <td className="text-maroon font-bold text-xs uppercase flex items-center pt-5">
                    {expandedId === order._id ? <ChevronUp size={14}/> : <ChevronDown size={14}/>} View Details
                  </td>
                </tr>
                {expandedId === order._id && (
                  <tr className="bg-gray-50 shadow-inner">
                    <td colSpan="8" className="p-6 border-b">
                      <div className="grid grid-cols-2 gap-8">
                        <div>
                          <h4 className="font-bold text-xs uppercase mb-3 flex items-center text-gray-500"><MapPin size={14} className="mr-1"/> Shipping Address</h4>
                          <p className="text-xs leading-relaxed">{order.customer?.address || '—'}</p>
                          {order.customer?.pincode && <p className="text-xs">Pincode: {order.customer.pincode}</p>}
                          {order.customer?.email && <p className="text-xs">Email: {order.customer.email}</p>}
                        </div>
                        <div>
                          <h4 className="font-bold text-xs uppercase mb-3 flex items-center text-gray-500"><Package size={14} className="mr-1"/> Items List</h4>
                          {order.items?.map((item, i) => (
                            <div
                            key={`${item.productId || item._id || item.name}-${i}`}
                            className="flex items-center justify-between text-xs border-b py-3 gap-3"
                            >
                              <div className="flex items-center gap-3">
                                {item.image ? (
                                  <img
                                  src={item.image}
                                  alt={item.name}
                                  className="w-12 h-14 object-cover border rounded"
                                  />
                                ) : (
                                <div className="w-12 h-14 border rounded bg-gray-100 flex items-center justify-center text-[9px] text-gray-400 text-center">
                                  No Image
                                </div>
                              )}
                                <div>
                                  <p className="font-bold text-[#3d0c11]">{item.name}</p>
                                  <p className="text-gray-500">Saree ID: {item.sareeId || "—"}</p>
                                  <p className="text-gray-500">Qty: {item.quantity}</p>
                                </div>
                              </div>
                              <span className="font-bold">₹{item.price * item.quantity}</span>
                            </div>
                          ))}
                          <div className="mt-2 text-xs">
                            <strong>Subtotal: ₹{order.subtotal}</strong><br/>
                            <strong>Total: ₹{order.total}</strong>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminOrders;